import axios from 'axios';
import { getSignedUrls, uploadFileToS3 } from './s3';

const API_BASE = `${process.env.REACT_APP_API_BASE_URL}/api/projects`;

export function flattenProject(apiData) {
  const parseDateForInput = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toISOString().split('T')[0];
    } catch (error) {
      return '';
    }
  };

  return {
    _id: apiData._id,
    projectId: apiData.projectId || apiData.projectNo || apiData.projectName,
    badge: apiData.badgeType || apiData.projectType,
    badgeColor: '#FFC107',
    name: apiData.projectName,
    projectName: apiData.projectName,
    developer: apiData.developerName,
    status: 'Active',
    commission: apiData.commission?.toString() || 'N/A',
    commissionMethod: apiData.commissionMethod || '',
    type: apiData.projectType,
    progressType: apiData.progressType || '',
    amenities: Array.isArray(apiData.amenities)
      ? apiData.amenities
      : apiData.amenities
        ? [apiData.amenities]
        : [],
    guidelineValue: apiData.projectDetails?.guidelineValue?.toString() || '',
    totalPlot: apiData.projectDetails?.totalInventory?.toString() || '0',
    totalBuilding: apiData.projectDetails?.totalInventory?.toString() || '0',
    totalUnits: apiData.projectDetails?.totalInventory?.toString() || '0',
    totalRowHouses: apiData.projectDetails?.totalInventory?.toString() || '0',
    totalVillas: apiData.projectDetails?.totalInventory?.toString() || '0',
    reraNo: apiData.projectDetails?.reraNumber || '',
    startDate: parseDateForInput(apiData.projectDetails?.startDate),
    completeDate: parseDateForInput(apiData.projectDetails?.completionDate),
    contactPerson: apiData.contact?.person || '',
    phone: apiData.contact?.phone || '',
    email: apiData.contact?.email || '',
    city: apiData.address?.city || '',
    state: apiData.address?.state || '',
    projectLogo: apiData.projectLogo || apiData.logo || '',
    projectLogoViewUrl: apiData.projectLogoViewUrl || apiData.logoViewUrl || apiData.projectLogo || apiData.logo || '',
    logo: apiData.projectLogo || apiData.logo || '',
    logoViewUrl: apiData.projectLogoViewUrl || apiData.logoViewUrl || apiData.projectLogo || apiData.logo || '',
    documents: Array.isArray(apiData.documents)
      ? apiData.documents.map((doc, index) => ({
        ...doc,
        id: doc._id || doc.id || `${apiData._id || 'project'}-doc-${index}`,
        serverId: doc._id || doc.serverId || null,
        isPersisted: Boolean(doc._id || doc.serverId || doc.fileKey || doc.fileUrl),
        fileKey: doc.fileKey || doc.fileUrl || '',
        fileUrl: doc.fileKey || doc.fileUrl || '',
        fileViewUrl: doc.fileViewUrl || doc.viewUrl || doc.fileKey || doc.fileUrl || '',
      }))
      : [],
    address:
      typeof apiData.address === 'string'
        ? apiData.address
        : apiData.address?.fullAddress ||
        apiData.address?.addressLine ||
        '',
    bankName: apiData.bankDetails?.bankName || '',
    accountHolderName: apiData.bankDetails?.accountHolderName || '',
    accountNumber: apiData.bankDetails?.accountNumber || '',
    ifscCode: apiData.bankDetails?.ifscCode || '',
    possessionDate: apiData.projectDetails?.possessionDate || '',
    rewardPoints: apiData?.rewardPoints || 0,
  };
}

export const uploadProjectLogo = async (file) => {
  if (!file) {
    return { key: '', viewUrl: '' };
  }

  const uploads = await getSignedUrls([
    {
      category: 'project',
      fileName: file.name,
      fileType: file.type || 'image/jpeg',
    }
  ]);

  const uploadMeta = uploads[0];
  if (!uploadMeta?.uploadUrl || !uploadMeta?.key) {
    throw new Error('Failed to generate signed upload URL for project logo');
  }

  const uploaded = await uploadFileToS3(
    uploadMeta.uploadUrl,
    file,
    file.type || uploadMeta.fileType || 'image/jpeg'
  );

  if (!uploaded) {
    throw new Error('Failed to upload project logo to S3');
  }

  return {
    key: uploadMeta.key,
    viewUrl: uploadMeta.viewUrl || '',
  };
};

export const uploadProjectDocument = async (file) => uploadProjectLogo(file);

export const getProjects = async (options = {}) => {
  try {
    const params = {};
    if (options.projectType) {
      params.projectType = options.projectType;
    }
    const response = await axios.get(API_BASE, { params });
    return response.data.data.map(flattenProject);
  } catch (error) {
    return [];
  }
};

// Fetch projects with pagination for dropdowns
export const getProjectsPaginated = async (params = {}) => {
  try {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await axios.get(API_BASE, {
      params,
      headers
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch projects');
  }
};

export const createProject = async (payload) => {
  try {
    const response = await axios.post(`${API_BASE}/create`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateProject = async (projectId, payload) => {
  try {
    const response = await axios.put(`${API_BASE}/edit/${projectId}`, payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteProjectDocument = async (projectId, documentId) => {
  try {
    const response = await axios.delete(`${API_BASE}/edit/${projectId}/documents/${documentId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteProject = async (projectId) => {
  try {
    const response = await axios.put(`${API_BASE}/edit/${projectId}`, { isDeleted: true });
    return response.data;
  } catch (error) {
    throw error;
  }
};



export const getPlotsByProject = async (projectId, inventoryType = "Plot", projectType, status, page = 1, limit = 50, search, excludePurchased, sortBy, sortOrder) => {
  try {
    const params = {};
    if (projectId) {
      params.projectId = projectId;
    }
    if (inventoryType) {
      params.inventoryType = inventoryType;
    }
    if (projectType) {
      params.projectType = projectType;
    }
    if (status) {
      params.status = status;
    }
    if (page) {
      params.page = page;
    }
    if (limit) {
      params.limit = limit;
    }
    if (search) {
      params.search = search;
    }
    if (excludePurchased !== undefined) {
      params.excludePurchased = excludePurchased;
    }
    if (sortBy) {
      params.sortBy = sortBy;
    }
    if (sortOrder) {
      params.sortOrder = sortOrder;
    }
    if (process.env.NODE_ENV !== 'production') {
      // Debug log to verify API invocation and params during development
      // eslint-disable-next-line no-console
      console.debug('getPlotsByProject → GET', `${API_BASE}/details`, params);
    }
    console.log('params', params);
    const response = await axios.get(`${API_BASE}/details`, { params });
    console.log('response', response);
    const raw = response?.data || {};

    const ok = raw.status === true || raw.success === true;
    if (!ok) {
      throw new Error(raw.message || 'Failed to fetch plots');
    }

    // Return full response with pagination data
    const dataBlock = raw.data || {};
    return {
      inventories: Array.isArray(dataBlock.inventories)
        ? dataBlock.inventories
        : Array.isArray(raw.inventories)
          ? raw.inventories
          : Array.isArray(raw.data)
            ? raw.data
            : [],
      pagination: dataBlock.pagination || raw.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: limit
      },
      totalAmount: dataBlock.totalAmount || raw.totalAmount || 0,
      availableCount: dataBlock.availableCount || raw.availableCount || 0,
      bookedCount: dataBlock.bookedCount || raw.bookedCount || 0,
      count: dataBlock.count || raw.count || 0
    };
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('getPlotsByProject error', error);
    }
    return {
      inventories: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: limit
      },
      totalAmount: 0,
      availableCount: 0,
      bookedCount: 0,
      count: 0
    };
  }
};


export const getPlotsByProjectSearch = async (projectId, inventoryType = "Plot", projectType, status, search = '', page = 1, limit = 20) => {
  try {
    const params = {};
    if (projectId) {
      params.projectId = projectId;
    }
    if (inventoryType) {
      params.inventoryType = inventoryType;
    }
    if (projectType) {
      params.projectType = projectType;
    }
    if (status) {
      params.status = status;
    }
    if (search) {
      params.search = search;
    }
    if (page) {
      params.page = page;
    }
    if (limit) {
      params.limit = limit;
    }
    if (process.env.NODE_ENV !== 'production') {
      // Debug log to verify API invocation and params during development
      // eslint-disable-next-line no-console
      console.debug('getPlotsByProject → GET', `${API_BASE}/details`, params);
    }
    console.log('params', params);
    const response = await axios.get(`${API_BASE}/details`, { params });
    console.log('response', response);
    const raw = response?.data || {};

    const ok = raw.status === true || raw.success === true;
    if (!ok) {
      throw new Error(raw.message || 'Failed to fetch plots');
    }

    // Return full response with pagination data
    const dataBlock = raw.data || {};
    return {
      inventories: Array.isArray(dataBlock.inventories)
        ? dataBlock.inventories
        : Array.isArray(raw.inventories)
          ? raw.inventories
          : Array.isArray(raw.data)
            ? raw.data
            : [],
      pagination: dataBlock.pagination || raw.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: limit
      },
      totalAmount: dataBlock.totalAmount || raw.totalAmount || 0,
      availableCount: dataBlock.availableCount || raw.availableCount || 0,
      bookedCount: dataBlock.bookedCount || raw.bookedCount || 0,
      count: dataBlock.count || raw.count || 0
    };
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('getPlotsByProject error', error);
    }
    return {
      inventories: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: limit
      },
      totalAmount: 0,
      availableCount: 0,
      bookedCount: 0,
      count: 0
    };
  }
};

export const searchFloorData = async ({ projectId, buildingId, inventoryType }) => {
  try {
    const params = {};
    if (projectId) params.projectId = projectId;
    if (buildingId) params.buildingId = buildingId;
    if (inventoryType) params.inventoryType = inventoryType;
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/floor`;
    if (process.env.NODE_ENV !== 'production') {
      console.debug('searchFloorData → GET', url, params);
    }
    const response = await axios.get(url, {
      params,
      headers: { 'Content-Type': 'application/json' },
    });
    const raw = response?.data;
    console.log('raw', raw);
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.floors)
          ? raw.floors
          : Array.isArray(raw?.items)
            ? raw.items
            : [];
    return list;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('searchFloorData error', error);
    }
    return [];
  }
};

export const searchFlatData = async ({ projectId, floorId, buildingId, inventoryType }) => {
  try {
    const params = {};
    if (projectId) params.projectId = projectId;
    if (floorId) params.floorId = floorId;
    if (buildingId) params.buildingId = buildingId;
    if (inventoryType) params.inventoryType = inventoryType;
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/flats`;
    if (process.env.NODE_ENV !== 'production') {
      console.debug('searchFlatData → GET', url, params);
    }
    const response = await axios.get(url, {
      params,
      headers: { 'Content-Type': 'application/json' },
    });
    const raw = response?.data;
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.flats)
          ? raw.flats
          : Array.isArray(raw?.items)
            ? raw.items
            : [];
    return list;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('searchFlatData error', error);
    }
    return [];
  }
};

export const updateFloor = async (floorId, payload) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/floor`;
    if (process.env.NODE_ENV !== 'production') {
      console.debug('updateFloor → PUT', url, floorId ? { id: floorId } : {}, payload);
    }
    const config = {
      headers: { 'Content-Type': 'application/json' },
    };
    if (floorId) {
      config.params = { id: floorId };
    }
    const response = await axios.put(url, payload, config);
    return response.data;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('updateFloor error', error);
    }
    throw error;
  }
};

export const createFloor = async (payload) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/floor`;
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('createFloor → POST', url, payload);
    }
    const response = await axios.post(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('createFloor error', error);
    }
    if (error.response?.data) {
      // surface backend error body to caller if needed
      throw error.response.data;
    }
    throw new Error(error.response?.data?.message || 'Failed to create floor');
  }
};

export const deleteFloor = async (floorId) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/floor`;
    if (process.env.NODE_ENV !== 'production') {
    }
    const response = await axios.put(url, { isDeleted: true }, {
      // Backend expects Mongo _id to come through
      params: { id: floorId },
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('deleteFloor error', error);
    }
    throw error;
  }
};


export const updatePlot = async (plotId, data) => {
  const response = await fetch(`${API_BASE}/inventory/${plotId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    let errorMessage = 'Failed to update plot';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
      console.error('API Error:', errorData);
    } catch (e) {
      console.error('Error parsing response:', e);
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

export const searchFlats = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      queryParams.append(key, value);
    }
  });

  const url = `${process.env.REACT_APP_API_BASE_URL}/api/flats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    let errorMessage = 'Failed to search flats';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
      console.error('API Error:', errorData);
    } catch (e) {
      console.error('Error parsing response:', e);
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

// Server-side inventory search for Inventory screen
export const searchInventory = async (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      value !== 'null'
    ) {
      queryParams.append(key, value);
    }
  });

  const base = process.env.REACT_APP_API_BASE_URL;
  const url = `${base}/api/projects/details${queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    let errorMessage = 'Failed to search inventory';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
      // eslint-disable-next-line no-console
      console.error('Inventory API Error:', errorData);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error parsing inventory response:', e);
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

export const updateFlat = async (flatId, data) => {
  const base = process.env.REACT_APP_API_BASE_URL;
  const response = await fetch(`${base}/api/flats/${flatId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    let errorMessage = 'Failed to update flat';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
      console.error('API Error:', errorData);
    } catch (e) {
      console.error('Error parsing response:', e);
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

export const createFlat = async (data) => {
  const base = process.env.REACT_APP_API_BASE_URL;
  const response = await fetch(`${base}/api/flats`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    let errorMessage = 'Failed to create flat';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
      // eslint-disable-next-line no-console
      console.error('API Error:', errorData);
      // If backend returns structured errors, expose them
      if (errorData && Array.isArray(errorData.errors)) {
        const err = new Error(errorMessage);
        err.errors = errorData.errors;
        throw err;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error parsing response:', e);
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

export const deleteFlat = async (flatId) => {
  const base = process.env.REACT_APP_API_BASE_URL;
  const response = await fetch(`${base}/api/flats/${flatId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ isDeleted: true }),
  });
  if (!response.ok) {
    let errorMessage = 'Failed to delete flat';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
      console.error('API Error:', errorData);
    } catch (e) {
      console.error('Error parsing response:', e);
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

export const createBooking = async (payload) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/bookings/create`;
    const response = await axios.post(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      // Server responded with error status
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to create booking');
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('No response from server. Please check your connection.');
    } else {
      // Error in setting up the request
      throw new Error(error.message || 'Failed to create booking');
    }
  }
};

export const getBookingById = async (id) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/bookings/${id}`;
    const response = await axios.get(url, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to fetch booking details');
    } else {
      throw new Error(error.message || 'Failed to fetch booking details');
    }
  }
};

export const downloadBookingFormPdf = async (bookingId) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/bookings/${bookingId}/booking-form`;
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;

    const response = await axios.get(url, {
      responseType: 'blob',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    return response.data;
  } catch (error) {
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed?.message || parsed?.error || 'Failed to download booking form PDF');
      } catch (parseError) {
        throw new Error('Failed to download booking form PDF');
      }
    }

    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to download booking form PDF');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to download booking form PDF');
    }
  }
};

export const downloadReceiptPdf = async (receiptId) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/transactions/receipt/${receiptId}/pdf`;
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;

    const response = await axios.get(url, {
      responseType: 'blob',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    return response.data;
  } catch (error) {
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed?.message || parsed?.error || 'Failed to download receipt PDF');
      } catch (parseError) {
        throw new Error('Failed to download receipt PDF');
      }
    }

    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to download receipt PDF');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to download receipt PDF');
    }
  }
};

export const getBookings = async (params = {}) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/bookings`;

    // Build query parameters
    const queryParams = { ...params };

    if (process.env.NODE_ENV !== 'production') {
      console.debug('getBookings → GET', url, queryParams);
    }

    const response = await axios.get(url, {
      params: queryParams,
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to fetch bookings');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to fetch bookings');
    }
  }
};

export const getCommissionGenerator = async (params = {}) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/CommissionGenerator`;

    // Build query parameters
    const queryParams = { ...params };

    if (process.env.NODE_ENV !== 'production') {
      console.debug('getCommissionGenerator → GET', url, queryParams);
    }

    const response = await axios.get(url, {
      params: queryParams,
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to fetch commission data');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to fetch commission data');
    }
  }
};

export const getBookingHierarchy = async (bookingId) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/CommissionGenerator/booking-hierarchy`;

    if (process.env.NODE_ENV !== 'production') {
      console.debug('getBookingHierarchy → GET', url, { bookingId });
    }

    const response = await axios.get(url, {
      params: { bookingId },
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to fetch booking hierarchy');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to fetch booking hierarchy');
    }
  }
};

export const updateCommissionGenerator = async (commissionId, payload) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/CommissionGenerator/${commissionId}`;

    if (process.env.NODE_ENV !== 'production') {
      console.debug('updateCommissionGenerator → PUT', url, payload);
    }

    const response = await axios.put(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to update commission');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to update commission');
    }
  }
};

export const updateBooking = async (bookingId, payload) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/bookings/${bookingId}`;
    const response = await axios.put(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to update booking');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to update booking');
    }
  }
};

export const updateBookingStatus = async (bookingId, payload) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/bookings/status/${bookingId}`;

    if (process.env.NODE_ENV !== 'production') {
      console.debug('updateBookingStatus → PUT', url, payload);
    }

    const response = await axios.put(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to update booking status');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to update booking status');
    }
  }
};

export const updateBookingApprovedStatus = async (bookingId, payload) => {
  try {
    const token = localStorage.getItem('token');
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/bookings/approved-status/${bookingId}`;

    const response = await axios.put(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to update booking status');
    } else {
      throw new Error(error.message || 'Failed to update booking status');
    }
  }
};

export const getBookingTransactions = async (bookingId, transactionType = null) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/transactions/booking`;
    const params = { bookingId, _id: bookingId };

    // Add transactionType if provided
    if (transactionType) {
      params.transactionType = transactionType;
    }

    const response = await axios.get(url, {
      params,
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to fetch transactions');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to fetch transactions');
    }
  }
};

export const createReceipt = async (payload) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/transactions/create-receipt`;
    const response = await axios.post(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to create receipt');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to create receipt');
    }
  }
};

export const deleteReceipt = async (receiptId, params = {}) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/bookings/receipt/${receiptId}`;

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;

    const response = await axios.delete(url, {
      params, // Passing as query parameters
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to delete receipt');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to delete receipt');
    }
  }
};

export const cancelBooking = async (payload) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/bookings/cancel`;
    const response = await axios.post(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to cancel booking');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to cancel booking');
    }
  }
};

// Installment plan APIs
export const getInstallmentPlan = async (bookingId) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/installments/${bookingId}`;

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;

    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to fetch installment plan');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to fetch installment plan');
    }
  }
};

export const createOrReplaceInstallmentPlan = async (payload) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/installments`;

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;

    const response = await axios.post(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to save installment plan');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to save installment plan');
    }
  }
};

export const updateInstallmentStatus = async (planId, installmentSrNo, payload) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/installments/${planId}/installments/${installmentSrNo}`;

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;

    // Use axios with explicit method configuration to avoid CORS issues
    const response = await axios({
      method: 'PATCH',
      url: url,
      data: payload,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to update installment status');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to update installment status');
    }
  }
};

// Booking Follow-up APIs
export const createBookingFollowUp = async (payload) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/booking-follow-ups`;

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;

    const response = await axios.post(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to create follow-up');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to create follow-up');
    }
  }
};

export const getBookingFollowUps = async (params = {}) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    let url = `${base}/api/booking-follow-ups`;

    // If bookingId is provided, use the specific booking endpoint
    if (params.bookingId) {
      url = `${base}/api/booking-follow-ups/booking/${params.bookingId}`;
    }

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;

    // Build query parameters (mostly for global fetch, but kept for flexibility)
    const queryParams = {};
    if (params.page) {
      queryParams.page = params.page;
    }
    if (params.limit) {
      queryParams.limit = params.limit;
    }

    const response = await axios.get(url, {
      params: queryParams,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to fetch follow-ups');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to fetch follow-ups');
    }
  }
};

// Booking Progress API
export const getBookingProgress = async (params = {}) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/booking-progress`;

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;

    // Build query parameters
    const queryParams = {};
    if (params.bookingId) {
      queryParams.bookingId = params.bookingId;
    }
    if (params.page) {
      queryParams.page = params.page;
    }
    if (params.limit) {
      queryParams.limit = params.limit;
    }

    const response = await axios.get(url, {
      params: queryParams,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to fetch booking progress');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to fetch booking progress');
    }
  }
};

export const getBookingDocuments = async (bookingId) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/bookings/${bookingId}/documents`;

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;

    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to fetch booking documents');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to fetch booking documents');
    }
  }
};

// Fly commission API for associate commission calculation
export const getFlyCommission = async (params = {}) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/bookings/flyCommission`;

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;

    const queryParams = {};
    if (params.associateId) {
      queryParams.associateId = params.associateId;
    }
    if (params.commissionMethod) {
      queryParams.commissionMethod = params.commissionMethod;
    }
    if (params.commission) {
      queryParams.commission = params.commission;
    }

    if (process.env.NODE_ENV !== 'production') {
      console.debug('getFlyCommission → GET', url, queryParams);
    }

    const response = await axios.get(url, {
      params: queryParams,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      // Allow backend to return 400 with useful body; don't throw automatically
      validateStatus: () => true,
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to fetch fly commission');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to fetch fly commission');
    }
  }
};

export const createRefundReceipt = async (payload) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/transactions/receipt/refund`;

    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;

    if (process.env.NODE_ENV !== 'production') {
      console.debug('createRefundReceipt → POST', url, payload);
    }

    const response = await axios.post(url, payload, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || error.response.data?.error || 'Failed to create refund receipt');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to create refund receipt');
    }
  }
};
export const getAllTransactions = async (params = {}) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/transactions`;
    const response = await axios.get(url, {
      params,
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch transactions');
  }
};

export const updateTransactionStatus = async (transactionId, payload) => {
  try {
    const base = process.env.REACT_APP_API_BASE_URL;
    const url = `${base}/api/transactions/status/${transactionId}`;
    const response = await axios.put(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to update transaction status');
  }
};
